import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  fetchAdminTenant,
  fetchAdminTenants,
  createAdminTenant,
  updateAdminTenant,
  fetchAdminTenantNotes,
  createAdminTenantNote,
  fetchAdminTenantEvents,
  fetchAdminTenancies,
  createAdminTenancy,
  patchAdminTenancy,
  fetchAdminTenancyRevenue,
  createAdminTenancyRevenue,
  patchAdminTenancyRevenue,
  deleteAdminTenancyRevenue,
  fetchAdminUnits,
  fetchAdminRooms,
  fetchAdminTenantDocuments,
  uploadAdminTenantDocument,
  fetchAdminTenantDocumentDownloadUrl,
  deleteAdminTenantDocument,
  fetchAdminAuditLogs,
  fetchAdminTenantAddresses,
  fetchAdminTasks,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import { useTheme } from "../../contexts/ThemeContext";
import { tenantDisplayName } from "../../utils/tenantDisplayName";
import { formatAuditLog, auditActionLabel, auditActorDisplay } from "../../utils/auditDisplay";
import { AuditChangeRows } from "../../components/admin/AuditLogVisual";
import { getDisplayUnitId } from "../../utils/unitDisplayId";
import {
  UNIT_LANDLORD_LEASE_ENDED_TENANCY_MESSAGE,
  deriveTenantOperationalStatus,
  getTodayIsoForOccupancy,
  parseIsoDate,
} from "../../utils/unitOccupancyStatus";
import {
  normalizeRevenueFrequency,
  monthlyEquivalentFromRevenueRows,
  totalOneTimeRevenueFromRows,
  recurringMonthlyBreakdownEntries,
  oneTimeBreakdownEntries,
} from "../../utils/tenancyRevenueBreakdown";
import {
  sectionCardClass,
  textareaClass,
  sectionTitleClass,
  tableClass,
  thCellClass,
  tdCellClass,
} from "./tenant/tenantDetailStyles";
import { formatDateTime, todayBusinessDate } from "../../utils/datetime";
import {
  parseAdminErrorFromResponse,
  formatTenantDocumentDate,
  formatTenantDocumentType,
  formatTenantAuditDateDe,
  formatTenantAuditChf,
  auditLogToTenantHistoryEvent,
  snapIntToAllowedList,
  tenancyLifecycleStatusLabelDe,
  formatNoticePeriodMonthsDe,
  formatMinimumDurationMonthsDe,
  dateOnlyOrNull,
  buildActualMoveOutPatchFields,
  tenancyDisplayEndIso,
  tenancyDisplayStatusLabelDe,
  tenancyDraftDisplayEndIso,
  deriveTenancyLifecyclePreviewForAssign,
  storedTenancyStatusForApi,
  tenancyEndUrgencyNote,
  formatChfRent,
  inspectionCheckStatusLabelDe,
  inspectionCheckStatusTextClass,
  summarizeMoveInInspection,
  summarizeMoveOutInspection,
  parseOptionalTenantDepositFloat,
  parseRevenueAmount,
  maxIsoDate,
  schedulingEndDateFromPartsJs,
  tenancySchedulingEndIso,
  roomOverlapsAssignProposal,
  assignRoomStatusSuffixDe,
  assignRoomOptionTitleDe,
  applyRecurringRevenueDatesFromTenancy,
  revenueRowZeitraumDisplay,
  deriveTenancyStatusFromDates,
  tenancyStatusLabelFromDerived,
  tenancyDateRangeLabel,
  secondParticipantZweitmieterLine,
  viewerTenancyRoleLabelDe,
  tenancyIncludesTenant,
  validateSecondParticipant,
  findOpenPrimaryTenancyForRoom,
  getStatusMeta,
  initialAssignSecondForm,
  readableUnitHeaderLabel,
} from "./tenant/tenantDetailHelpers";
import TenantNotesBlock from "./tenant/TenantNotesBlock";
import TenantHistoryBlock from "./tenant/TenantHistoryBlock";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import InspectionChecklistModal from "./tenant/InspectionChecklistModal";
import TenantStammdatenEditForm from "./tenant/TenantStammdatenEditForm";
import TenantAddressesSection from "./tenant/TenantAddressesSection";
import TenancyCard from "./tenant/TenancyCard";
import DepositEditModal from "./tenant/overview/DepositEditModal";
import DepositPayoutModal from "./tenant/overview/DepositPayoutModal";
import {
  TENANCY_GROUPS,
  TENANCY_GROUP_LABELS,
  groupTenancies,
  tenancyGroup,
} from "./tenant/tenancies/tenancyViewSelectors";
import TenancyAssignForm from "./tenant/TenancyAssignForm";
import TenantMergeModal from "../../components/admin/tenants/TenantMergeModal";
import TenantOverviewPanel from "./tenant/overview/TenantOverviewPanel";
import { OPEN_TASK_STATUSES, selectOpenTasks } from "./tenant/overview/overviewSelectors";
import { useTenantInspections } from "./hooks/useTenantInspections";
import { DetailTabs, DetailTabPanel } from "@/components/ui/DetailTabs";
import TenantRequirementsTab from "./tenant/requirements/TenantRequirementsTab";
import TenantInvoicesTab from "./tenant/invoices/TenantInvoicesTab";
import useTenantInvoiceOverview from "./tenant/invoices/useTenantInvoiceOverview";
import useInvoiceCapabilities from "./invoices/useInvoiceCapabilities";
import EntityTasksTab from "./tasks/EntityTasksTab";
import EntityWorkReportsTab from "./tasks/EntityWorkReportsTab";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "../../contexts/AuthContext";


const TENANT_DOCUMENT_CATEGORY_LABELS = {
  rent_contract: "Mietvertrag",
  id_document: "Ausweiskopie",
  debt_register: "Betreibungsregisterauszug",
  insurance: "Versicherung",
  liability_insurance: "Privathaftpflicht",
  employment_contract: "Arbeitsvertrag",
  pay_stub: "Lohnabrechnung",
  residence_permit: "Aufenthaltsbewilligung",
  handover_protocol: "Übergabeprotokoll",
  other: "Sonstiges",
};

function formatTenantDocumentCategoryLabel(category) {
  if (category == null || String(category).trim() === "") return "—";
  const k = String(category).trim();
  return TENANT_DOCUMENT_CATEGORY_LABELS[k] || k;
}


const MIN_DURATION_MONTHS_ALLOWED = [0, 1, 2, 3, 6, 12];
const NOTICE_PERIOD_MONTHS_ALLOWED = [0, 1, 2, 3];

const pageWrapClass = "mx-auto max-w-[min(1400px,100%)]";


/**
 * Tenant-level status badge, one tone per status KEY.
 *
 * Cancelled gets the rose family the tenant-detail surface already uses for a
 * cancelled tenancy (`TenancySummaryHeader`, `tenantDetailStyles`) rather than
 * a new colour: "Vor Einzug storniert" and "Ausgezogen" are the two states this
 * page most needs to keep apart, and the neutral fallback greys were within a
 * shade of each other.
 */
const TENANT_OP_BADGE_BASE =
  "inline-flex shrink-0 items-center rounded-full border px-2 py-[2px] text-[9px] font-semibold";

const TENANT_OP_BADGE_TONE = {
  active:
    "border-[rgba(61,220,132,0.35)] bg-[rgba(61,220,132,0.14)] text-[#3ddc84]",
  reserved:
    "border-[rgba(245,166,35,0.35)] bg-[rgba(245,166,35,0.12)] text-[#f5a623]",
  ended: "border-slate-500/35 bg-slate-800/90 text-slate-200",
  cancelled: "border-rose-500/35 bg-rose-500/10 text-rose-300",
};

const TENANT_OP_BADGE_FALLBACK =
  "border-slate-500/30 bg-slate-800/70 text-slate-300";

const PERMIT_OPTIONS = new Set(["B", "C", "L", "G", "Other"]);


const emptyForm = {
  firstName: "",
  lastName: "",
  birthDate: "",
  nationality: "",
  isSwiss: null,
  residencePermit: "",
  email: "",
  phone: "",
  company: "",
  street: "",
  postalCode: "",
  city: "",
  country: "",
  languages: [],
  iban: "",
  // Migration 172. True = this tenant gets NO automatic birthday greeting.
  // Starts false so a form rendered before the tenant loads never reads as an
  // opt-out that nobody asked for.
  birthdayEmailOptOut: false,
};

function tenantToForm(t) {
  if (!t) return { ...emptyForm };
  const bd = t.birth_date;
  const birthDate =
    bd && typeof bd === "string" && /^\d{4}-\d{2}-\d{2}/.test(bd)
      ? bd.slice(0, 10)
      : "";
  const rp = t.residence_permit || "";
  return {
    firstName: (t.first_name || "").trim(),
    lastName: (t.last_name || "").trim(),
    birthDate,
    nationality: (t.nationality || "").trim(),
    isSwiss:
      t.is_swiss === true ? true : t.is_swiss === false ? false : null,
    residencePermit: PERMIT_OPTIONS.has(rp) ? rp : "",
    email: (t.email || "").trim(),
    phone: (t.phone || "").trim(),
    company: (t.company || "").trim(),
    street: (t.street || "").trim(),
    postalCode: (t.postal_code || "").trim(),
    city: (t.city || "").trim(),
    country: (t.country || "").trim(),
    languages: Array.isArray(t.languages) ? t.languages : [],
    iban: (t.iban || "").trim(),
    // `=== true` rather than a truthy test: the field is a NOT NULL boolean on
    // the wire, and anything else reaching here (a stale cached tenant from
    // before migration 172, a fixture that omits it) means "not opted out".
    birthdayEmailOptOut: t.birthday_email_opt_out === true,
  };
}

const TENANT_DETAIL_TABS = [
  { id: "overview", label: "Übersicht" },
  // M1a: lazyMount. Every expanded tenancy card mounts children that fetch —
  // contract, key receipt, handover preview, documents, claims, mutations — and
  // before this they all ran on the tenant page's first render, for a tab most
  // readers never open. Deferring the panel until the tab is first activated
  // moves that cost to the moment it buys something. Once activated the panel
  // stays mounted, so switching tabs still does not refetch.
  { id: "tenancies", label: "Mietverhältnisse", lazyMount: true },
  // S7 — beside Mietverhältnisse because it reads the same contracts from a
  // different angle: what each one still owes. lazyMount for the same reason
  // as its neighbour, the panel issues a request of its own and opening a
  // tenant must not pay for a tab nobody looked at.
  { id: "requirements", label: "Nachweise & Pflichten", lazyMount: true },
  // TINV-2: lazyMount. The panel owns its own read now and performs it on
  // activation, so mounting it eagerly would restore exactly the ungated
  // page-mount fetch this slice removed — invisible, because a mount-all
  // panel is merely hidden by CSS.
  { id: "invoices", label: "Rechnungen", lazyMount: true },
  { id: "notes", label: "Notizen" },
  { id: "documents", label: "Dokumente" },
  { id: "tasks", label: "Aufgaben", lazyMount: true },
  // D2a — next to Aufgaben because both answer "what work happened for
  // this tenant"; lazyMount so opening a tenant does not fetch reports
  // for a tab nobody looked at (same treatment as Aufgaben).
  { id: "reports", label: "Rapporte", lazyMount: true },
  { id: "history", label: "Aktivität" },
];

// Landlord fixed-term cap (Phase 3a.5e-B): mirrors backend _unit_fixed_term_end.
// Returns the Unit's lease_end_date (ISO) only for fixed-term landlord
// contracts; otherwise null. No hardcoded date.
export function unitFixedTermEnd(unit) {
  if (
    unit &&
    String(unit.leaseType || "") === "fixed_term" &&
    unit.leaseEndDate
  ) {
    return unit.leaseEndDate;
  }
  return null;
}

export default function AdminTenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  // Logged-in admin — used to prefill the management signer name in the
  // inspection signature panel (the modal itself stays props-in / no context).
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const editing = Boolean(
    location.state &&
      typeof location.state === "object" &&
      location.state.tenantStammdatenEdit === true
  );
  const [tenant, setTenant] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  // Edit is triggered globally from the header; the edit form lives only
  // in the Übersicht panel, so entering edit mode lands the user there.
  useEffect(() => {
    if (editing) setActiveTab("overview");
  }, [editing]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [tenantDocuments, setTenantDocuments] = useState([]);
  const [tenantDocUploading, setTenantDocUploading] = useState(false);
  const [tenantDocUploadError, setTenantDocUploadError] = useState("");
  const [tenantDocCategory, setTenantDocCategory] = useState("");
  const tenantDocFileInputRef = useRef(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteValidationErr, setNoteValidationErr] = useState(null);
  const [noteSubmitError, setNoteSubmitError] = useState(null);
  // O1a: the address-book rows, lifted from TenantAddressesSection so the
  // Übersicht card can show the row the contract marks `is_current` instead of
  // the flat legacy mirror. Same single request as before — the section is now
  // mounted on demand rather than eagerly, so the count is unchanged.
  const [tenantAddresses, setTenantAddresses] = useState([]);
  // "The book is empty" and "we could not read the book" are different facts.
  // Collapsing them is what let a failed request render as the definitive
  // sentence "Keine Wohnadresse im Adressbuch erfasst".
  const [addressesError, setAddressesError] = useState(null);
  // A read started for tenant A must not write its answer into the page after
  // the user has moved on to tenant B. The mount effect has its own `cancelled`
  // closure for this; reloads fired from callbacks outside an effect do not, so
  // they compare against the tenant the page is actually showing.
  const activeTenantIdRef = useRef(tenantId);
  useEffect(() => {
    activeTenantIdRef.current = tenantId;
  }, [tenantId]);
  const [addressHistoryOpen, setAddressHistoryOpen] = useState(false);
  const [openTasks, setOpenTasks] = useState(null);
  const [openTasksError, setOpenTasksError] = useState(null);
  const documentsPagination = usePagination(tenantDocuments, {
    pageSize: 10,
  });
  const [tenancies, setTenancies] = useState([]);
  // M1a: which tenancy cards are open. Collapsed cards render no child
  // section, and the heavy children are the ones that fetch — so this set is
  // what keeps a tenant with five tenancies from firing five times the
  // per-tenancy requests. Seeded from the grouped list below.
  const [expandedTenancyIds, setExpandedTenancyIds] = useState(() => new Set());

  /*
    K4d — which tenancy the deposit editors are aimed at, or null.

    THE ROW ITSELF, not an id. Both modals re-seed their form on
    `[open, tenancy]`, so the object identity is load-bearing: an id plus a
    `tenancies.find(...)` in the render body would hand them a NEW object on
    every parent render and wipe whatever the user had typed. Holding the row
    the card passed up keeps that identity stable until the list is actually
    refetched — and by then the modal has closed, because a successful save
    closes it.

    Two separate selections rather than one `{ type, tenancy }`: the two
    editors are independent, and a shared slot would need a discriminator read
    in four places to say the same thing.
  */
  const [depositEditTenancy, setDepositEditTenancy] = useState(null);
  const [depositPayoutTenancy, setDepositPayoutTenancy] = useState(null);
  const [expandSeededFor, setExpandSeededFor] = useState(null);
  // Bumped after every tenancy mutation so an open activity feed reloads
  // instead of showing rows from before the change (same refreshKey pattern
  // AdminTaskDetailPage and AdminWorkReportDetailPage already use).
  const [tenancyActivityKey, setTenancyActivityKey] = useState(0);
  const [shouldRefreshTenantList, setShouldRefreshTenantList] = useState(false);

  // Units-by-id map for fixed-term cap resolution (assign + edit). Loaded once.
  const [unitsById, setUnitsById] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [assignUnits, setAssignUnits] = useState([]);
  const [assignUnitsLoading, setAssignUnitsLoading] = useState(false);
  const [assignUnitsErr, setAssignUnitsErr] = useState(null);
  const [assignRooms, setAssignRooms] = useState([]);
  const [assignRoomsLoading, setAssignRoomsLoading] = useState(false);
  const [assignUnitTenancies, setAssignUnitTenancies] = useState([]);
  const [assignUnitId, setAssignUnitId] = useState("");
  const [assignRoomId, setAssignRoomId] = useState("");
  const [assignMoveIn, setAssignMoveIn] = useState("");
  // Befristet bis / Vertragsende → move_out_date (NOT a cancellation signal).
  const [assignMoveOut, setAssignMoveOut] = useState("");
  const [assignNoticeGivenAt, setAssignNoticeGivenAt] = useState("");
  const [assignTerminationEffective, setAssignTerminationEffective] = useState("");
  const [assignActualMoveOut, setAssignActualMoveOut] = useState("");
  const [assignTerminatedBy, setAssignTerminatedBy] = useState("");
  // Variante B: cancellation fields hidden behind a deliberate toggle. Collapse
  // is UI-only and never clears values.
  const [assignCancellationOpen, setAssignCancellationOpen] = useState(false);
  const [assignTenantDepositType, setAssignTenantDepositType] = useState("");
  const [assignTenantDepositAmount, setAssignTenantDepositAmount] = useState("");
  const [assignTenantDepositProvider, setAssignTenantDepositProvider] = useState("");
  const [assignMinimumDurationMonths, setAssignMinimumDurationMonths] = useState("3");
  const [assignNoticePeriodMonths, setAssignNoticePeriodMonths] = useState("1");
  const [assignErr, setAssignErr] = useState(null);
  const [assignSecondInlineOpen, setAssignSecondInlineOpen] = useState(false);
  const [assignSecondForm, setAssignSecondForm] = useState(initialAssignSecondForm);
  const [assignSecondRole, setAssignSecondRole] = useState("");
  // Cluster A / A1: second person can be an existing tenant (picked via search,
  // NO new tenant row) or a new tenant (inline form). "existing" is the default
  // so the dedup path is the obvious one.
  const [assignSecondMode, setAssignSecondMode] = useState("existing");
  const [assignSecondSelectedTenant, setAssignSecondSelectedTenant] = useState(null);
  const [assignSecondSearchQuery, setAssignSecondSearchQuery] = useState("");
  const [assignSecondSearchResults, setAssignSecondSearchResults] = useState([]);
  const [assignSecondSearchLoading, setAssignSecondSearchLoading] = useState(false);
  const [assignSecondSearchErr, setAssignSecondSearchErr] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignRevenueRows, setAssignRevenueRows] = useState([]);
  const [assignRevenueForm, setAssignRevenueForm] = useState({
    type: "rent",
    amount_chf: "",
    frequency: "monthly",
    start_date: "",
    end_date: "",
    notes: "",
  });

  const [tenancyEditingId, setTenancyEditingId] = useState(null);
  // Mietbeginn / Einzug → move_in_date. Original captured on open for the dirty
  // check (only send move_in_date when it actually changed) and the confirm dialog.
  const [tenancyEditMoveIn, setTenancyEditMoveIn] = useState("");
  const [tenancyEditMoveInOriginal, setTenancyEditMoveInOriginal] = useState("");
  // Befristet bis / Vertragsende → move_out_date.
  const [tenancyEditMoveOut, setTenancyEditMoveOut] = useState("");
  // KND-2: "open" now gates only the physical return date (actual_move_out_date).
  // The three termination fields have no state here any more — the termination
  // block in the Mietvertrag card owns them (discovery E2).
  const [tenancyEditCancellationOpen, setTenancyEditCancellationOpen] = useState(false);
  const [tenancyEditActualMoveOut, setTenancyEditActualMoveOut] = useState("");
  const [tenancyEditMinimumDurationMonths, setTenancyEditMinimumDurationMonths] = useState("3");
  const [tenancyEditNoticePeriodMonths, setTenancyEditNoticePeriodMonths] = useState("1");
  const [tenancyEditSaving, setTenancyEditSaving] = useState(false);
  const [tenancyEditErr, setTenancyEditErr] = useState(null);

  const tenancyEditSectionRef = useRef(null);
  const assignRoomDefaultsPrevIdRef = useRef(null);

  const [tenancyRevenueByTenancyId, setTenancyRevenueByTenancyId] = useState({});
  const [tenancyRevenueLoadingId, setTenancyRevenueLoadingId] = useState(null);
  const [tenancyRevenueErr, setTenancyRevenueErr] = useState(null);
  const [revenueEditingId, setRevenueEditingId] = useState(null);
  const [revenueForm, setRevenueForm] = useState({
    type: "rent",
    amount_chf: "",
    frequency: "monthly",
    start_date: "",
    end_date: "",
    notes: "",
  });

  const {
    inspectionsList,
    inspectionDetailById,
    inspectionModal,
    openOrCreateInspection,
    saveInspectionItemRow,
    uploadInspectionItemPhotos,
    deleteInspectionItemPhoto,
    finalizeInspection,
    generateProtocolPdf,
    openProtocolPdf,
    signInspectionDualAndOpenPdf,
    openSignedInspectionPdf,
    closeInspectionModal,
    documentsRefreshSignal,
  } = useTenantInspections(tenantId, tenancies);

  /*
    TINV-2 — WHICH INVOICE WORLD THIS ORGANIZATION BILLS IN.

    Read ONCE, here, and handed down. Both consumers need the same answer: the
    Übersicht's Finanzen card, to know what it may claim, and the Rechnungen tab,
    to know what to load and what to call it. Two independent reads would be two
    requests per tenant AND two copies of a loading and error state free to
    disagree for as long as one of them was in flight — which is the shape the
    invoice dashboard removed from its own page for exactly this reason.

    `nativeCreationEnabled` is deliberately NOT forwarded. That boolean is
    fail-closed to `false` while loading and after a failure, which is correct for
    hiding a create button and wrong for deciding which world someone is reading:
    it would collapse "this org bills in Bexio" and "we do not know yet" into one
    value. Nothing on this page creates an invoice, so the boolean has no consumer
    here at all.
  */
  const {
    provider: invoiceProvider,
    loading: invoiceCapabilitiesLoading,
    error: invoiceCapabilitiesError,
  } = useInvoiceCapabilities();

  /*
    The Finanzen card's own small read — NOT the tab's.

    The card is on the default tab and therefore always visible, so it pays for a
    count on mount; the tab pays for a page of ten only when it is opened. The
    hook decides which endpoint that count comes from, and reads nothing at all
    until the provider is confirmed.
  */
  const invoiceOverview = useTenantInvoiceOverview({
    tenantId,
    provider: invoiceProvider,
    capabilitiesLoading: invoiceCapabilitiesLoading,
    capabilitiesError: invoiceCapabilitiesError,
  });

  const prefetchTenancyRevenueForTenancyList = useCallback(async (tenancyList) => {
    const ids = (tenancyList || [])
      .map((t) => t?.id)
      .filter((id) => id != null && String(id).trim() !== "")
      .map(String);
    if (!ids.length) {
      setTenancyRevenueByTenancyId({});
      return;
    }
    const results = await Promise.all(
      ids.map(async (tid) => {
        try {
          const rows = await fetchAdminTenancyRevenue(tid);
          return [tid, Array.isArray(rows) ? rows : []];
        } catch {
          return [tid, []];
        }
      })
    );
    const next = {};
    for (const [tid, rows] of results) next[tid] = rows;
    setTenancyRevenueByTenancyId(next);
  }, []);

  const mergedHistoryEvents = useMemo(() => {
    const fromAudit = (auditLogs || []).map(auditLogToTenantHistoryEvent).filter(Boolean);
    const combined = [...(events || []), ...fromAudit];
    return combined.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [events, auditLogs]);

  const reloadTenanciesForTenant = useCallback(async () => {
    setTenancyActivityKey((k) => k + 1);
    try {
      const items = await fetchAdminTenancies({
        tenant_id: tenantId,
        limit: 200,
        include_participant: true,
        include_cancelled: true,
      });
      const list = Array.isArray(items) ? items : [];
      setTenancies(list);
      await prefetchTenancyRevenueForTenancyList(list);
      return list;
    } catch {
      setTenancies([]);
      setTenancyRevenueByTenancyId({});
      return [];
    }
  }, [tenantId, prefetchTenancyRevenueForTenancyList]);

  const reloadTenantAuditLogs = useCallback(async () => {
    if (!tenantId) return;
    try {
      const auditData = await fetchAdminAuditLogs({
        entity_type: "tenant",
        entity_id: tenantId,
      });
      setAuditLogs(Array.isArray(auditData?.items) ? auditData.items : []);
    } catch {
      setAuditLogs([]);
    }
  }, [tenantId]);

  // The Übersicht card and the address-book section read the same rows, so every
  // mutation that can change them refreshes through here: one request, one error
  // rule, no drift between the two surfaces.
  //
  // This never rejects. Callers chain it next to a save, and a refresh that
  // fails must not be reported as a save that failed.
  const reloadTenantAddresses = useCallback(async () => {
    const requestedTenantId = tenantId;
    if (!requestedTenantId) return;
    try {
      const rows = await fetchAdminTenantAddresses(requestedTenantId);
      if (activeTenantIdRef.current !== requestedTenantId) return;
      setTenantAddresses(Array.isArray(rows) ? rows : []);
      setAddressesError(null);
    } catch (err) {
      if (activeTenantIdRef.current !== requestedTenantId) return;
      console.error(
        "AdminTenantDetailPage: fetchAdminTenantAddresses failed",
        err
      );
      // Deliberately NOT setTenantAddresses([]): a failed refresh must not
      // delete what is already correct on screen. The card shows the error.
      setAddressesError("Adressen konnten nicht geladen werden.");
    }
  }, [tenantId]);

  // Regel 49: the Verlauf merges tenant_events + tenant audit_logs. Any mutation
  // that emits a history entry must reload BOTH streams, not just the tenant row.
  const reloadTenantHistory = useCallback(async () => {
    if (!tenantId) return;
    const [eData] = await Promise.all([
      fetchAdminTenantEvents(tenantId).catch(() => null),
      reloadTenantAuditLogs(),
    ]);
    if (eData?.items) setEvents(eData.items);
  }, [tenantId, reloadTenantAuditLogs]);

  /*
    K4d — what a saved deposit or payout refreshes.

    Exactly the contract the Übersicht used to hold (K4x.3a), moved here with
    the modals and not rewritten: both editors save through the same tenancy
    PATCH the big form uses, so the rows are re-read, and the patch writes an
    audit entry on the tenant stream, so the Verlauf is re-read too (Regel 49).

    `useCallback` because it is a modal prop: an inline arrow would be a new
    function every render, and these two modals are the ones whose re-seed
    effect makes identity matter.
  */
  const reloadAfterDepositSave = useCallback(async () => {
    await reloadTenanciesForTenant();
    reloadTenantHistory();
  }, [reloadTenanciesForTenant, reloadTenantHistory]);

  // After a successful merge: refresh ONLY (the success toast is owned by the
  // modal). Reload the survivor row (scalar fills), its tenancies, and the
  // Verlauf (events + audit, Regel 49).
  const handleTenantMerged = useCallback(async () => {
    if (!tenantId) return;
    await Promise.all([
      fetchAdminTenant(tenantId)
        .then((t) => {
          if (t) setTenant(t);
        })
        .catch(() => null),
      reloadTenanciesForTenant(),
      reloadTenantHistory(),
    ]);
  }, [tenantId, reloadTenanciesForTenant, reloadTenantHistory]);

  const cancelTenancyEdit = () => {
    setTenancyEditingId(null);
    setTenancyEditErr(null);
    setTenancyEditMoveIn("");
    setTenancyEditMoveInOriginal("");
    setTenancyEditMoveOut("");
    setTenancyEditCancellationOpen(false);
    setTenancyEditActualMoveOut("");
    setTenancyEditMinimumDurationMonths("3");
    setTenancyEditNoticePeriodMonths("1");
    setTenancyRevenueErr(null);
    setRevenueEditingId(null);
    setRevenueForm({
      type: "rent",
      amount_chf: "",
      frequency: "monthly",
      start_date: "",
      end_date: "",
      notes: "",
    });
  };

  const startTenancyEdit = (tn) => {
    setTenancyEditingId(String(tn.id));
    setTenancyEditErr(null);
    // Befristet bis = move_out_date. Termination is its OWN field — do NOT seed
    // it from move_out_date (that overload is exactly what mislabels fixed-term
    // tenancies as "Gekündigt").
    setTenancyEditMoveOut(dateOnlyOrNull(tn.move_out_date) || "");
    const miInit = dateOnlyOrNull(tn.move_in_date) || "";
    setTenancyEditMoveIn(miInit);
    setTenancyEditMoveInOriginal(miInit);
    const actInit = dateOnlyOrNull(tn.actual_move_out_date) || "";
    setTenancyEditActualMoveOut(actInit);
    // Auto-open the return section only when a return date is stored. A
    // termination no longer opens it: the termination is not edited here.
    setTenancyEditCancellationOpen(Boolean(actInit));
    setTenancyEditMinimumDurationMonths(
      String(snapIntToAllowedList(tn.minimum_duration_months, MIN_DURATION_MONTHS_ALLOWED, 3))
    );
    setTenancyEditNoticePeriodMonths(
      String(snapIntToAllowedList(tn.notice_period_months, NOTICE_PERIOD_MONTHS_ALLOWED, 1))
    );

    const tid = tn?.id != null ? String(tn.id) : "";
    setTenancyRevenueErr(null);
    setRevenueEditingId(null);
    const mi = dateOnlyOrNull(tn.move_in_date) || "";
    const mo = tenancyDisplayEndIso(tn) || "";
    setRevenueForm({
      type: "rent",
      amount_chf: "",
      frequency: "monthly",
      start_date: mi,
      end_date: mo || "",
      notes: "",
    });
    if (tid && tenancyRevenueByTenancyId?.[tid] == null) {
      setTenancyRevenueLoadingId(tid);
      fetchAdminTenancyRevenue(tid)
        .then((rows) => {
          setTenancyRevenueByTenancyId((prev) => ({
            ...(prev || {}),
            [tid]: Array.isArray(rows) ? rows : [],
          }));
        })
        .catch((err) => setTenancyRevenueErr(err?.message || "Einnahmen konnten nicht geladen werden."))
        .finally(() => setTenancyRevenueLoadingId(null));
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tenancyEditSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  const submitTenancyEdit = () => {
    if (!tenancyEditingId || !tenantId) return;
    setTenancyEditErr(null);
    setTenancyEditSaving(true);
    // KND-2: notice_given_at, termination_effective_date and terminated_by are
    // NOT sent — the termination block in the Mietvertrag card owns them, and a
    // key this form does not send cannot overwrite (or null) what that block
    // saved (TenancyPatch/exclude_unset). The return date keeps its old rule: a
    // closed "Rückgabe" section sends explicit null. move_out_date (Befristet bis
    // / Vertragsende) is a SEPARATE field and stays untouched by this gate.
    const body = {
      move_out_date: dateOnlyOrNull(tenancyEditMoveOut),
      ...buildActualMoveOutPatchFields(
        tenancyEditCancellationOpen,
        tenancyEditActualMoveOut
      ),
      minimum_duration_months: snapIntToAllowedList(
        tenancyEditMinimumDurationMonths,
        MIN_DURATION_MONTHS_ALLOWED,
        3
      ),
      notice_period_months: snapIntToAllowedList(
        tenancyEditNoticePeriodMonths,
        NOTICE_PERIOD_MONTHS_ALLOWED,
        1
      ),
    };
    // Dirty check (Slice 1): only include move_in_date when the normalized ISO
    // value actually changed. dateOnlyOrNull collapses null / "" / "YYYY-MM-DD" /
    // timestamp serializations to a comparable date-only string. An emptied field
    // is treated as "no change" (move_in_date is mandatory and cannot be cleared
    // via this field); the confirm dialog in TenancyCard uses the same comparison.
    const _miNew = dateOnlyOrNull(tenancyEditMoveIn);
    const _miOld = dateOnlyOrNull(tenancyEditMoveInOriginal);
    if (_miNew && _miNew !== _miOld) {
      body.move_in_date = _miNew;
    }
    patchAdminTenancy(tenancyEditingId, body)
      .then(() =>
        Promise.all([
          reloadTenanciesForTenant(),
          fetchAdminTenantEvents(tenantId),
          reloadTenantAuditLogs(),
        ])
      )
      .then(([, eData]) => {
        if (eData?.items) setEvents(eData.items);
        cancelTenancyEdit();
      })
      .catch((err) => {
        setTenancyEditErr(err?.message || "Speichern fehlgeschlagen.");
      })
      .finally(() => setTenancyEditSaving(false));
  };

  /**
   * The tab's four buckets, derived from server fields only (`is_running` and
   * `display_status`). Ordering inside a bucket is the server's — it sorts by
   * move_in_date DESC with a documented tie-break, and re-sorting here would
   * invent a second ordering. Empty buckets are dropped so the tab does not
   * show four headings for one tenancy.
   */
  const tenancyGroupsOrdered = useMemo(() => {
    const grouped = groupTenancies(tenancies);
    return TENANCY_GROUPS.map((key) => ({
      key,
      label: TENANCY_GROUP_LABELS[key],
      items: grouped[key],
    })).filter((g) => g.items.length > 0);
  }, [tenancies]);

  /**
   * Open the current tenancies by default, once per tenant.
   *
   * "Current" is `is_running`, so an internal move opens both of the
   * overlapping tenancies rather than hiding one. Seeding is keyed on the
   * tenant id so a reload of the list does not re-open what the reader closed.
   */
  useEffect(() => {
    if (!tenantId || !tenancies.length) return;
    if (expandSeededFor === tenantId) return;
    const running = tenancies
      .filter((t) => tenancyGroup(t) === "current")
      .map((t) => (t.id != null ? String(t.id) : `${t.move_in_date}-${t.room_id}`));
    setExpandedTenancyIds(new Set(running));
    setExpandSeededFor(tenantId);
  }, [tenantId, tenancies, expandSeededFor]);

  const toggleTenancyExpanded = useCallback((rowKey) => {
    setExpandedTenancyIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const goToTenantList = () =>
    navigate(
      "/admin/tenants",
      shouldRefreshTenantList ? { state: { refreshTenants: true } } : undefined
    );

  const cancelTenantStammdatenEdit = () => navigate(-1);

  /**
   * Enter Stammdaten edit mode.
   *
   * Extracted verbatim from the header button's inline handler (O1a) because the
   * Übersicht's "Profil & Kontakt" card now offers the same action: one
   * definition means the two entry points cannot drift apart. Behaviour is
   * unchanged — a history entry carrying `tenantStammdatenEdit`, which is what
   * `editing` reads and what `cancelTenantStammdatenEdit` pops.
   */
  const startTenantStammdatenEdit = () => {
    setSaveError(null);
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        state: {
          ...(typeof location.state === "object" && location.state !== null
            ? location.state
            : {}),
          tenantStammdatenEdit: true,
        },
      }
    );
  };

  useEffect(() => {
    if (
      !(
        location.state &&
        typeof location.state === "object" &&
        location.state.tenantStammdatenEdit === true
      ) &&
      tenant
    ) {
      setForm(tenantToForm(tenant));
      setSaveError(null);
    }
  }, [location.state, tenant]);

  const startRevenueEdit = (row) => {
    if (!row?.id) return;
    setRevenueEditingId(String(row.id));
    const rf = normalizeRevenueFrequency(row.frequency);
    const isOneTime = rf === "one_time";
    setRevenueForm({
      type: String(row.type || "").trim() || "rent",
      amount_chf: row.amount_chf != null ? String(row.amount_chf) : "",
      frequency: rf,
      start_date: row.start_date != null ? String(row.start_date).slice(0, 10) : "",
      end_date: isOneTime && row.end_date != null ? String(row.end_date).slice(0, 10) : "",
      notes: row.notes != null ? String(row.notes) : "",
    });
    setTenancyRevenueErr(null);
  };

  const cancelRevenueEdit = (tn) => {
    const parentTenancyId = tenancyEditingId;
    setRevenueEditingId(null);
    if (!tn) {
      setRevenueForm({
        type: "rent",
        amount_chf: "",
        frequency: "monthly",
        start_date: "",
        end_date: "",
        notes: "",
      });
      setTenancyRevenueErr(null);
      return;
    }
    const mi = dateOnlyOrNull(tn.move_in_date) || "";
    const moDb = tenancyDisplayEndIso(tn) || "";
    const tid = String(tn.id);
    const moForm =
      parentTenancyId && String(parentTenancyId) === tid
        ? tenancyDraftDisplayEndIso(tenancyEditActualMoveOut, tn.termination_effective_date)
        : "";
    const moEff = moForm || moDb;
    setRevenueForm({
      type: "rent",
      amount_chf: "",
      frequency: "monthly",
      start_date: mi,
      end_date: moEff || "",
      notes: "",
    });
    setTenancyRevenueErr(null);
  };

  const submitRevenueForm = async (tenancyId, tnForDefaults) => {
    const tid = tenancyId != null ? String(tenancyId) : "";
    if (!tid) return;
    const type = String(revenueForm.type || "").trim();
    if (!type) {
      setTenancyRevenueErr("Bitte Typ angeben.");
      return;
    }
    const amt = parseRevenueAmount(revenueForm.amount_chf);
    if (amt == null) {
      setTenancyRevenueErr("Bitte einen gültigen Betrag (≠ 0) eingeben.");
      return;
    }
    const freq = normalizeRevenueFrequency(revenueForm.frequency);
    let start_date = dateOnlyOrNull(revenueForm.start_date);
    if (freq !== "one_time" && !start_date) {
      start_date = dateOnlyOrNull(tnForDefaults?.move_in_date) || null;
    }
    const end_date = freq === "one_time" ? dateOnlyOrNull(revenueForm.end_date) : null;
    if (freq === "one_time" && start_date && end_date && end_date < start_date) {
      setTenancyRevenueErr("Enddatum muss nach dem Startdatum liegen.");
      return;
    }
    const body = {
      type,
      amount_chf: amt,
      frequency: freq,
      start_date,
      end_date,
      notes: String(revenueForm.notes || "").trim() || null,
    };
    setTenancyRevenueErr(null);
    setTenancyRevenueLoadingId(tid);
    try {
      if (revenueEditingId) {
        await patchAdminTenancyRevenue(revenueEditingId, body);
      } else {
        await createAdminTenancyRevenue(tid, body);
      }
      const list = await reloadTenanciesForTenant();
      await reloadTenantAuditLogs();
      const fresh = Array.isArray(list) ? list.find((x) => String(x.id) === tid) : null;
      cancelRevenueEdit(fresh || tnForDefaults || null);
    } catch (err) {
      setTenancyRevenueErr(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setTenancyRevenueLoadingId(null);
    }
  };

  const deleteRevenueRow = async (tenancyId, row, tnForDefaults) => {
    const tid = tenancyId != null ? String(tenancyId) : "";
    const rid = row?.id != null ? String(row.id) : "";
    if (!tid || !rid) return;
    if (!window.confirm("Diesen Einnahmen-Eintrag wirklich löschen?")) return;
    setTenancyRevenueErr(null);
    setTenancyRevenueLoadingId(tid);
    try {
      await deleteAdminTenancyRevenue(rid);
      const list = await reloadTenanciesForTenant();
      await reloadTenantAuditLogs();
      if (revenueEditingId === rid) {
        const fresh = Array.isArray(list) ? list.find((x) => String(x.id) === tid) : null;
        cancelRevenueEdit(fresh || tnForDefaults || null);
      }
    } catch (err) {
      setTenancyRevenueErr(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setTenancyRevenueLoadingId(null);
    }
  };

  function handleTenantDocPick() {
    tenantDocFileInputRef.current?.click();
  }

  async function handleTenantDocSelected(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !tenantId) return;
    setTenantDocUploading(true);
    setTenantDocUploadError("");
    try {
      await uploadAdminTenantDocument(tenantId, f, {
        category: tenantDocCategory.trim() || undefined,
      });
      setTenantDocCategory("");
      const items = await fetchAdminTenantDocuments(tenantId);
      setTenantDocuments(Array.isArray(items) ? items : []);
      await reloadTenantAuditLogs();
    } catch (err) {
      setTenantDocUploadError(err.message || "Upload fehlgeschlagen.");
    } finally {
      setTenantDocUploading(false);
    }
  }

  async function handleOpenTenantDocument(docId) {
    try {
      const data = await fetchAdminTenantDocumentDownloadUrl(docId);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(err.message || "Download konnte nicht gestartet werden.");
    }
  }

  async function handleDeleteTenantDocument(docId) {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await deleteAdminTenantDocument(docId);
      const items = await fetchAdminTenantDocuments(tenantId);
      setTenantDocuments(Array.isArray(items) ? items : []);
      await reloadTenantAuditLogs();
    } catch (err) {
      window.alert(err.message || "Löschen fehlgeschlagen.");
    }
  }

  useEffect(() => {
    if (!tenantId) {
      setTenant(null);
      setLoadError("Kein Mieter angegeben.");
      setSaveError(null);
      setNotes([]);
      setEvents([]);
      setAuditLogs([]);
      setTenantDocuments([]);
      setTenantAddresses([]);
      setAddressesError(null);
      setOpenTasks(null);
      setOpenTasksError(null);
      setTenancies([]);
      setNoteDraft("");
      setNoteValidationErr(null);
      setNoteSubmitError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setShouldRefreshTenantList(false);
    (async () => {
      try {
        const t = await fetchAdminTenant(tenantId);
        if (cancelled) return;
        if (!t) {
          setLoadError("Mieter nicht gefunden.");
          setTenant(null);
          setNotes([]);
          setEvents([]);
          setAuditLogs([]);
          setTenantDocuments([]);
          setTenantAddresses([]);
          setAddressesError(null);
          setOpenTasks(null);
          setOpenTasksError(null);
          setTenancies([]);
          return;
        }
        setTenant(t);
        setForm(tenantToForm(t));
        const tenanciesFetch = fetchAdminTenancies({
          tenant_id: tenantId,
          limit: 200,
          include_participant: true,
          include_cancelled: true,
        })
          .then((items) => (Array.isArray(items) ? items : []))
          .catch((err) => {
            console.error("AdminTenantDetailPage: fetchAdminTenancies failed", err);
            Sentry.captureException(err);
            return [];
          });
        const [
          nData,
          eData,
          tenancyItems,
          auditData,
          tdDocs,
          addrRows,
          taskData,
        ] = await Promise.all([
          fetchAdminTenantNotes(tenantId),
          fetchAdminTenantEvents(tenantId),
          tenanciesFetch,
          fetchAdminAuditLogs({ entity_type: "tenant", entity_id: tenantId }).catch(() => ({
            items: [],
          })),
          fetchAdminTenantDocuments(tenantId).catch(() => []),
          // O1a: the address book. TenantAddressesSection used to make exactly
          // this call on mount; it is now mounted on demand, so this is a move,
          // not an extra round trip.
          fetchAdminTenantAddresses(tenantId).catch((err) => {
            console.error(
              "AdminTenantDetailPage: fetchAdminTenantAddresses failed",
              err
            );
            return { __error: true };
          }),
          // O1a: the only genuinely new read. Batched into this Promise.all so
          // it costs no extra round trip, and failure-isolated so a task outage
          // never blanks the whole Übersicht.
          fetchAdminTasks({
            tenant_id: tenantId,
            status: OPEN_TASK_STATUSES,
            limit: 5,
          }).catch((err) => {
            console.error("AdminTenantDetailPage: fetchAdminTasks failed", err);
            return { __error: true };
          }),
        ]);
        if (cancelled) return;
        setNotes(nData?.items || []);
        setEvents(eData?.items || []);
        if (addrRows?.__error) {
          // Initial load, so there is nothing worth keeping: whatever sits in
          // state belongs to a tenant we have just navigated away from.
          setTenantAddresses([]);
          setAddressesError("Adressen konnten nicht geladen werden.");
        } else {
          setTenantAddresses(Array.isArray(addrRows) ? addrRows : []);
          setAddressesError(null);
        }
        if (taskData?.__error) {
          setOpenTasks(null);
          setOpenTasksError("Aufgaben konnten nicht geladen werden.");
        } else {
          setOpenTasks(selectOpenTasks(taskData));
          setOpenTasksError(null);
        }
        const tList = Array.isArray(tenancyItems) ? tenancyItems : [];
        setTenancies(tList);
        await prefetchTenancyRevenueForTenancyList(tList);
        setAuditLogs(Array.isArray(auditData?.items) ? auditData.items : []);
        setTenantDocuments(Array.isArray(tdDocs) ? tdDocs : []);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || "Laden fehlgeschlagen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, prefetchTenancyRevenueForTenancyList]);

  useEffect(() => {
    if (!assignOpen || !tenantId) return;
    setAssignUnitsErr(null);
    setAssignUnitsLoading(true);
    fetchAdminUnits()
      .then((data) => setAssignUnits((data || []).map(normalizeUnit)))
      .catch((e) => setAssignUnitsErr(e?.message ?? "Einheiten konnten nicht geladen werden."))
      .finally(() => setAssignUnitsLoading(false));
  }, [assignOpen, tenantId]);

  // Cluster A / A1: debounced existing-tenant search for the co-tenant picker.
  // Lives in the container (not the presentational form) and excludes the
  // current page tenant — selecting a result reuses its id, no createAdminTenant.
  useEffect(() => {
    if (!assignOpen || !assignSecondInlineOpen || assignSecondMode !== "existing") {
      return;
    }
    const q = String(assignSecondSearchQuery || "").trim();
    if (q.length < 2) {
      setAssignSecondSearchResults([]);
      setAssignSecondSearchLoading(false);
      setAssignSecondSearchErr(null);
      return;
    }
    let cancelled = false;
    setAssignSecondSearchLoading(true);
    setAssignSecondSearchErr(null);
    const handle = setTimeout(() => {
      fetchAdminTenants({ q, limit: 8 })
        .then((data) => {
          if (cancelled) return;
          const items = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : [];
          setAssignSecondSearchResults(
            items.filter((x) => String(x.id) !== String(tenantId))
          );
        })
        .catch((e) => {
          if (cancelled) return;
          setAssignSecondSearchErr(e?.message || "Mieter konnten nicht geladen werden.");
          setAssignSecondSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setAssignSecondSearchLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    assignOpen,
    assignSecondInlineOpen,
    assignSecondMode,
    assignSecondSearchQuery,
    tenantId,
  ]);

  // Load all units once for fixed-term-cap resolution (assign + edit forms).
  useEffect(() => {
    if (!tenantId) return;
    fetchAdminUnits()
      .then((data) => {
        const map = {};
        for (const u of (data || []).map(normalizeUnit)) {
          map[String(u.id)] = u;
        }
        setUnitsById(map);
      })
      .catch(() => setUnitsById({}));
  }, [tenantId]);

  const getUnitById = (id) => unitsById[String(id)] || null;

  useEffect(() => {
    if (!assignUnitId) {
      setAssignRooms([]);
      setAssignRoomId("");
      setAssignUnitTenancies([]);
      return;
    }
    setAssignRoomsLoading(true);
    Promise.all([
      fetchAdminRooms(assignUnitId)
        .then((raw) => (Array.isArray(raw) ? raw : []))
        .catch(() => []),
      fetchAdminTenancies({ unit_id: assignUnitId, limit: 200 })
        .then((items) => (Array.isArray(items) ? items : []))
        .catch(() => []),
    ])
      .then(([roomRaw, tenRows]) => {
        setAssignRooms(roomRaw.map(normalizeRoom));
        setAssignUnitTenancies(tenRows);
      })
      .finally(() => setAssignRoomsLoading(false));
  }, [assignUnitId]);

  useEffect(() => {
    if (!assignRoomId || assignRoomsLoading) return;
    const r = assignRooms.find((x) => String(x.id) === String(assignRoomId));
    if (!r) return;
    // Date-aware only (Slice A, inclusive end dates): there is NO same-day handover —
    // a currently belegt / reserviert room becomes selectable only after
    // move_out_date + 1 (the first free day past the existing tenancy's effective
    // end). Mirror the dropdown's disable rule in TenancyAssignForm; the helper is a
    // UX precheck, not the source of truth — clear only on a real date overlap.
    const overlaps = roomOverlapsAssignProposal(
      r,
      assignUnitTenancies,
      assignMoveIn,
      assignMoveOut
    );
    if (overlaps) setAssignRoomId("");
  }, [
    assignRoomId,
    assignRooms,
    assignRoomsLoading,
    assignUnitTenancies,
    assignMoveIn,
    assignMoveOut,
  ]);

  useEffect(() => {
    if (!assignOpen) {
      assignRoomDefaultsPrevIdRef.current = null;
      return;
    }
    if (!assignRoomId) {
      setAssignMinimumDurationMonths("3");
      setAssignNoticePeriodMonths("1");
      assignRoomDefaultsPrevIdRef.current = null;
      return;
    }
    if (assignRoomsLoading) return;
    const r = assignRooms.find((x) => String(x.id) === String(assignRoomId));
    if (!r) return;
    const prev = assignRoomDefaultsPrevIdRef.current;
    const roomChanged = String(prev) !== String(assignRoomId);
    assignRoomDefaultsPrevIdRef.current = assignRoomId;
    if (!roomChanged) return;
    const rawMin = r.default_minimum_stay_months ?? r.defaultMinimumStayMonths;
    const rawNp = r.default_notice_period_months ?? r.defaultNoticePeriodMonths;
    setAssignMinimumDurationMonths(
      String(snapIntToAllowedList(rawMin ?? 3, MIN_DURATION_MONTHS_ALLOWED, 3))
    );
    setAssignNoticePeriodMonths(
      String(snapIntToAllowedList(rawNp ?? 1, NOTICE_PERIOD_MONTHS_ALLOWED, 1))
    );
  }, [assignOpen, assignRoomId, assignRooms, assignRoomsLoading]);

  useEffect(() => {
    if (tenancyEditingId == null) return undefined;
    const id = requestAnimationFrame(() => {
      tenancyEditSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(id);
  }, [tenancyEditingId]);

  const saveNote = () => {
    const text = noteDraft.trim();
    if (!text) {
      setNoteValidationErr("Bitte eine Notiz eingeben.");
      return;
    }
    setNoteValidationErr(null);
    setNoteSubmitError(null);
    setNoteSaving(true);
    createAdminTenantNote(tenantId, text)
      .then(() => {
        setNoteDraft("");
        return Promise.all([fetchAdminTenantNotes(tenantId), fetchAdminTenantEvents(tenantId)]);
      })
      .then(([nData, eData]) => {
        setNotes(nData?.items || []);
        setEvents(eData?.items || []);
      })
      .catch((err) => {
        console.warn("tenant note save failed", err);
        const m = String(err?.message || "");
        const technical =
          /body stream already read|Failed to execute ['"]text['"]/i.test(m);
        setNoteSubmitError(
          technical ? "Notiz konnte nicht gespeichert werden." : m || "Notiz konnte nicht gespeichert werden."
        );
      })
      .finally(() => setNoteSaving(false));
  };

  // The tenant-level status as a KEY ("active" | "reserved" | "ended" |
  // "cancelled" | "inactive"), kept separate from its German label. The badge
  // tone below switches on this key; it used to switch on `statusMeta.label`,
  // which meant a wording change silently changed a colour too, and a status
  // whose label was not in the chain fell into the neutral fallback unnoticed.
  const tenantOperationalStatus = useMemo(() => {
    const todayIso = getTodayIsoForOccupancy();
    // Participant-inclusive: a co-tenant is only in tenancy.participants[],
    // not the primary tenant_id. Primary-only filtering returned [] for
    // co-tenants → deriveTenantOperationalStatus("inactive") → wrong "Inaktiv"
    // badge despite an active tenancy.
    const mine = Array.isArray(tenancies)
      ? tenancies.filter((x) => tenancyIncludesTenant(x, tenantId))
      : [];
    return deriveTenantOperationalStatus(mine, todayIso);
  }, [tenancies, tenantId]);

  const statusMeta = useMemo(
    () => getStatusMeta(tenantOperationalStatus),
    [tenantOperationalStatus]
  );

  const applyUpdate = (updated) => {
    setTenant(updated);
    setForm(tenantToForm(updated));
  };

  const setField = (key) => (e) => {
    if (key === "isSwiss") {
      const raw = e.target.value;
      const v = raw === "" ? null : raw === "true";
      setForm((f) => {
        const next = { ...f, isSwiss: v };
        if (v === true) next.residencePermit = "";
        return next;
      });
      return;
    }
    // Checkbox, not a text input: `e.target.value` on a checkbox is the static
    // `value` attribute ("on"), never the checked state, so reading it would
    // store a truthy string and the toggle would only ever switch on.
    if (key === "birthdayEmailOptOut") {
      const checked = e.target.checked === true;
      setForm((f) => ({ ...f, birthdayEmailOptOut: checked }));
      return;
    }
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const resetAssignForm = () => {
    const today = todayBusinessDate();
    setAssignUnitId("");
    setAssignRoomId("");
    setAssignMoveIn(today);
    setAssignMoveOut("");
    setAssignNoticeGivenAt("");
    setAssignTerminationEffective("");
    setAssignActualMoveOut("");
    setAssignTerminatedBy("");
    setAssignCancellationOpen(false);
    setAssignTenantDepositType("");
    setAssignTenantDepositAmount("");
    setAssignTenantDepositProvider("");
    setAssignMinimumDurationMonths("3");
    setAssignNoticePeriodMonths("1");
    setAssignSecondInlineOpen(false);
    setAssignSecondForm(initialAssignSecondForm());
    setAssignSecondRole("");
    setAssignSecondMode("existing");
    setAssignSecondSelectedTenant(null);
    setAssignSecondSearchQuery("");
    setAssignSecondSearchResults([]);
    setAssignSecondSearchLoading(false);
    setAssignSecondSearchErr(null);
    setAssignErr(null);
    setAssignRevenueRows([]);
    setAssignRevenueForm({
      type: "rent",
      amount_chf: "",
      frequency: "monthly",
      start_date: today,
      end_date: "",
      notes: "",
    });
  };

  const openAssignForm = () => {
    resetAssignForm();
    setAssignOpen(true);
  };

  const handleAssignSubmit = (e) => {
    e.preventDefault();
    setAssignErr(null);
    if (!assignUnitId || !assignRoomId || !assignMoveIn.trim()) {
      setAssignErr("Einheit, Zimmer und Einzugsdatum sind erforderlich.");
      return;
    }
    const rows = Array.isArray(assignRevenueRows) ? assignRevenueRows : [];
    for (const r of rows) {
      const type = String(r?.type || "").trim();
      if (!type) {
        setAssignErr("Bitte Typ für alle Einnahmen-Zeilen angeben.");
        return;
      }
      const amt = parseRevenueAmount(r?.amount_chf);
      if (amt == null) {
        setAssignErr("Bitte für alle Einnahmen-Zeilen einen gültigen Betrag (≠ 0) angeben.");
        return;
      }
      const freq = normalizeRevenueFrequency(r?.frequency);
      if (!["monthly", "yearly", "one_time"].includes(freq)) {
        setAssignErr("Bitte eine gültige Frequenz wählen.");
        return;
      }
      const sd = dateOnlyOrNull(r?.start_date);
      const ed = dateOnlyOrNull(r?.end_date);
      if (freq === "one_time" && sd && ed && ed < sd) {
        setAssignErr("Enddatum muss nach dem Startdatum liegen.");
        return;
      }
    }
    const assignUnit = assignUnits.find((x) => String(x.id) === String(assignUnitId));
    if (
      assignUnit &&
      String(assignUnit.leaseStatus ?? assignUnit.lease_status ?? "").trim() === "ended"
    ) {
      setAssignErr(UNIT_LANDLORD_LEASE_ENDED_TENANCY_MESSAGE);
      return;
    }
    if (assignSecondInlineOpen) {
      const v = validateSecondParticipant({
        mode: assignSecondMode,
        selectedTenantId: assignSecondSelectedTenant?.id,
        firstName: assignSecondForm.firstName,
        lastName: assignSecondForm.lastName,
        role: assignSecondRole,
      });
      if (!v.ok) {
        setAssignErr(v.error);
        return;
      }
    }
    const preview = deriveTenancyLifecyclePreviewForAssign(
      assignMoveIn.trim(),
      assignTerminationEffective,
      assignActualMoveOut
    );
    const derivedStatus = storedTenancyStatusForApi(preview);
    const tdt = String(assignTenantDepositType || "").trim().toLowerCase();
    const bodyBase = {
      tenant_id: String(tenantId),
      unit_id: String(assignUnitId),
      room_id: String(assignRoomId),
      move_in_date: assignMoveIn.trim(),
      move_out_date: dateOnlyOrNull(assignMoveOut),
      notice_given_at: dateOnlyOrNull(assignNoticeGivenAt),
      termination_effective_date: dateOnlyOrNull(assignTerminationEffective),
      actual_move_out_date: dateOnlyOrNull(assignActualMoveOut),
      terminated_by: String(assignTerminatedBy || "").trim().toLowerCase() || null,
      status: derivedStatus || "active",
      minimum_duration_months: snapIntToAllowedList(
        assignMinimumDurationMonths,
        MIN_DURATION_MONTHS_ALLOWED,
        3
      ),
      notice_period_months: snapIntToAllowedList(
        assignNoticePeriodMonths,
        NOTICE_PERIOD_MONTHS_ALLOWED,
        1
      ),
    };
    if (tdt) bodyBase.tenant_deposit_type = tdt;
    const tda = parseOptionalTenantDepositFloat(assignTenantDepositAmount);
    if (tda !== null) bodyBase.tenant_deposit_amount = tda;
    const tprov = String(assignTenantDepositProvider || "").trim().toLowerCase();
    if (tdt === "insurance" && tprov) bodyBase.tenant_deposit_provider = tprov;

    setAssignSaving(true);
    void (async () => {
      try {
        let secondTenantId = null;
        let roleForParticipants = "";
        if (assignSecondInlineOpen) {
          roleForParticipants = String(assignSecondRole || "").trim();
          if (assignSecondMode === "existing") {
            // A1 dedup path: reuse the picked tenant's id — NO createAdminTenant,
            // so no duplicate tenant row is created.
            secondTenantId =
              assignSecondSelectedTenant?.id != null
                ? String(assignSecondSelectedTenant.id)
                : "";
            if (!secondTenantId) {
              setAssignErr("Bitte einen bestehenden Mieter auswählen.");
              return;
            }
          } else {
            const fn2 = String(assignSecondForm.firstName || "").trim();
            const ln2 = String(assignSecondForm.lastName || "").trim();
            const permit2 =
              assignSecondForm.isSwiss === true
                ? undefined
                : assignSecondForm.residencePermit
                  ? assignSecondForm.residencePermit
                  : undefined;
            const createdSecond = await createAdminTenant({
              first_name: fn2,
              last_name: ln2,
              email: String(assignSecondForm.email || "").trim() || undefined,
              phone: String(assignSecondForm.phone || "").trim() || undefined,
              street: String(assignSecondForm.street || "").trim() || undefined,
              postal_code: String(assignSecondForm.postalCode || "").trim() || undefined,
              city: String(assignSecondForm.city || "").trim() || undefined,
              country: String(assignSecondForm.country || "").trim() || undefined,
              nationality: String(assignSecondForm.nationality || "").trim() || undefined,
              ...(assignSecondForm.isSwiss === null ? {} : { is_swiss: assignSecondForm.isSwiss }),
              residence_permit: permit2,
              room_id: assignRoomId ? String(assignRoomId) : undefined,
            });
            secondTenantId =
              createdSecond?.id != null ? String(createdSecond.id) : "";
            if (!secondTenantId) {
              setAssignErr("Zweitmieter konnte nicht angelegt werden.");
              return;
            }
          }
        }
        const participantsPayload =
          secondTenantId && roleForParticipants
            ? [
                { tenant_id: String(tenantId), role: "primary_tenant" },
                { tenant_id: secondTenantId, role: roleForParticipants },
              ]
            : null;
        if (participantsPayload) {
          const existingTenancy = findOpenPrimaryTenancyForRoom(
            tenancies,
            assignUnitId,
            assignRoomId
          );
          if (existingTenancy?.id != null) {
            await patchAdminTenancy(String(existingTenancy.id), {
              participants: participantsPayload,
            });
            const [, eData] = await Promise.all([
              reloadTenanciesForTenant(),
              fetchAdminTenantEvents(tenantId),
              reloadTenantAuditLogs(),
            ]);
            if (eData?.items) setEvents(eData.items);
            setAssignOpen(false);
            resetAssignForm();
            return;
          }
        }
        const body = { ...bodyBase };
        if (participantsPayload) {
          body.participants = participantsPayload;
        }
        const createdTenancy = await createAdminTenancy(body);
        const tid = createdTenancy?.id != null ? String(createdTenancy.id) : "";
        if (tid) {
          for (const r of rows) {
            const fr = normalizeRevenueFrequency(r.frequency);
            await createAdminTenancyRevenue(tid, {
              type: String(r.type || "").trim(),
              amount_chf: Number(String(r.amount_chf).replace(",", ".")),
              frequency: fr,
              start_date: dateOnlyOrNull(r.start_date),
              end_date: fr === "one_time" ? dateOnlyOrNull(r.end_date) : null,
              notes: String(r.notes || "").trim() || null,
            });
          }
        }
        const [, eData] = await Promise.all([
          reloadTenanciesForTenant(),
          fetchAdminTenantEvents(tenantId),
          reloadTenantAuditLogs(),
        ]);
        if (eData?.items) setEvents(eData.items);
        setAssignOpen(false);
        resetAssignForm();
      } catch (err) {
        setAssignErr(err?.message || "Speichern fehlgeschlagen.");
      } finally {
        setAssignSaving(false);
      }
    })();
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaveError(null);
    const fn = form.firstName.trim();
    const ln = form.lastName.trim();
    if (!fn || !ln) {
      setSaveError("Vor- und Nachname sind erforderlich.");
      return;
    }
    setSaving(true);
    updateAdminTenant(tenantId, {
      first_name: fn,
      last_name: ln,
      birth_date: form.birthDate.trim() || null,
      nationality: form.nationality.trim() || null,
      is_swiss: form.isSwiss,
      residence_permit:
        form.isSwiss === true
          ? null
          : form.residencePermit
            ? form.residencePermit
            : null,
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      company: form.company.trim() || null,
      street: form.street.trim() || null,
      postal_code: form.postalCode.trim() || null,
      city: form.city.trim() || null,
      country: form.country.trim() || null,
      languages: form.languages,
      iban: form.iban.trim() || null,
      // Always sent as a real boolean. `null` is a 422 on this field — the
      // column is NOT NULL and has no "unknown" state — so it must never be
      // routed through the `|| null` idiom the optional strings above use.
      birthday_email_opt_out: form.birthdayEmailOptOut === true,
    })
      .then((updated) => {
        applyUpdate(updated);
        navigate(-1);
        setShouldRefreshTenantList(true);
        return Promise.all([
          fetchAdminTenantEvents(tenantId).catch(() => null),
          reloadTenantAuditLogs(),
          // The PATCH can create or update the current address row
          // (tenant_address_service.sync_current_address_from_tenant_patch),
          // so the card has to re-read it. Exactly one extra request, and it
          // cannot reject — see reloadTenantAddresses.
          reloadTenantAddresses(),
        ]);
      })
      .then(([eData]) => {
        if (eData?.items) setEvents(eData.items);
      })
      .catch((err) => setSaveError(err?.message || "Speichern fehlgeschlagen."))
      .finally(() => setSaving(false));
  };

  const displayName = tenant ? tenantDisplayName(tenant) : "—";
  const permitReadLabel = (t) => {
    const p = t?.residence_permit;
    if (!p) return "—";
    return PERMIT_OPTIONS.has(p) ? p : `${p} (legacy)`;
  };

  const tenantOpBadgeClass = `${TENANT_OP_BADGE_BASE} ${
    TENANT_OP_BADGE_TONE[tenantOperationalStatus] || TENANT_OP_BADGE_FALLBACK
  }`;

  return (
    <div className="-m-6 min-h-screen bg-[#080a0f] text-[#edf0f7]">
      <div className={pageWrapClass}>
        {/*
          O1a QA fix: the row was `h-[50px]` with a `shrink-0` action group, so
          below ~640px the breadcrumb's flex item collapsed to zero width while
          its own `shrink-0` children kept their size — the name and the status
          badge painted straight over the three buttons. Wrapping instead of
          collapsing gives each group its own line on small screens; from `sm`
          up the geometry is byte-identical to before (one 50px row).
        */}
        <div className="sticky top-0 z-30 flex min-h-[50px] flex-wrap items-center gap-y-2 border-b border-[#1c2035] bg-[#0c0e15] px-6 py-2 backdrop-blur-md sm:flex-nowrap sm:py-0">
          <div className="flex min-w-0 flex-1 basis-full items-center gap-[12px] sm:basis-auto">
            <button
              type="button"
              onClick={() => {
                if (editing) {
                  cancelTenantStammdatenEdit();
                  return;
                }
                goToTenantList();
              }}
              className="shrink-0 rounded-[6px] border border-[#252a3a] bg-[#141720] px-[10px] py-[4px] text-[11px] text-[#4a5070] transition-colors hover:border-[#242840] hover:text-[#edf0f7]"
              style={{ cursor: "pointer" }}
            >
              {editing ? "Abbrechen" : "← Zurück"}
            </button>
            <div className="h-5 w-px shrink-0 bg-[#1c2035]" aria-hidden />
            <span className="shrink-0 text-[10px] text-[#4a5070]">Mieter</span>
            <span className="shrink-0 text-[#1c2035]">/</span>
            <span className="min-w-0 truncate text-[14px] font-medium text-[#edf0f7]">
              {loading ? "…" : displayName}
            </span>
            {!loading && tenant ? (
              <span className={tenantOpBadgeClass}>{statusMeta?.label || "Status"}</span>
            ) : null}
          </div>
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-[8px]">
            {!editing && tenant && !loadError && !loading ? (
              <>
                <button
                  type="button"
                  onClick={startTenantStammdatenEdit}
                  className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] transition-colors hover:border-[#242840] hover:text-[#edf0f7]"
                  style={{ cursor: "pointer" }}
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  onClick={handleTenantDocPick}
                  disabled={tenantDocUploading || !tenantId}
                  className="rounded-[6px] border border-[rgba(91,156,246,0.35)] bg-[rgba(91,156,246,0.12)] px-[14px] py-[4px] text-[11px] font-medium text-[#5b9cf6] transition-colors hover:border-[rgba(91,156,246,0.5)] hover:bg-[rgba(91,156,246,0.22)] disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ cursor: tenantDocUploading || !tenantId ? "not-allowed" : "pointer" }}
                >
                  Dokument erstellen
                </button>
                <button
                  type="button"
                  onClick={() => setMergeOpen(true)}
                  disabled={!tenantId}
                  className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] transition-colors hover:border-[#242840] hover:text-[#edf0f7] disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ cursor: !tenantId ? "not-allowed" : "pointer" }}
                >
                  Mieter zusammenführen
                </button>
              </>
            ) : null}
          </div>
        </div>

        <main>
          {loading ? (
            <p className="px-6 py-5 text-[#4a5070]">Lade Daten …</p>
          ) : loadError ? (
            <div className="mx-6 my-5 rounded-[12px] border border-[rgba(255,95,109,0.25)] bg-[rgba(255,95,109,0.08)] p-4 text-[#ff5f6d]">
              <p style={{ margin: "0 0 12px 0" }}>{loadError}</p>
              <button
                type="button"
                onClick={goToTenantList}
                className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] hover:border-[#242840] hover:text-[#edf0f7]"
                style={{ cursor: "pointer" }}
              >
                Zurück zur Übersicht
              </button>
            </div>
          ) : tenant ? (
            <DetailTabs
              tabs={TENANT_DETAIL_TABS}
              value={activeTab}
              onValueChange={setActiveTab}
              accent="blue"
            >
              <DetailTabPanel tabId="overview">
                {editing ? (
                  <div className="rounded-2xl bg-background p-4 sm:p-5">
                    <TenantStammdatenEditForm
                      form={form}
                      setField={setField}
                      setLanguages={(next) => setForm((f) => ({ ...f, languages: next }))}
                      saving={saving}
                      saveError={saveError}
                      onSubmit={handleSave}
                    />
                  </div>
                ) : (
                  <TenantOverviewPanel
                    tenant={tenant}
                    permitLabel={tenant?.residence_permit ? permitReadLabel(tenant) : null}
                    loading={loading}
                    loadError={loadError}
                    addresses={tenantAddresses}
                    addressesError={addressesError}
                    addressHistoryOpen={addressHistoryOpen}
                    onToggleAddressHistory={() => setAddressHistoryOpen((v) => !v)}
                    addressSection={
                      tenant ? (
                        <TenantAddressesSection
                          tenantId={tenantId}
                          tenant={tenant}
                          onCurrentChanged={() => {
                            fetchAdminTenant(tenantId)
                              .then((t) => {
                                if (t) applyUpdate(t);
                              })
                              .catch(() => null);
                            // The summary card reads the same rows, so it has to
                            // see the change the section just made.
                            reloadTenantAddresses();
                            // Regel 49: address actions emit a tenant_event —
                            // reload the Verlauf streams so it appears without a
                            // manual page reload.
                            reloadTenantHistory();
                          }}
                        />
                      ) : null
                    }
                    tenancies={tenancies}
                    invoiceOverview={invoiceOverview}
                    inspectionsList={inspectionsList}
                    tenantDocumentCount={tenantDocuments.length}
                    historyEvents={mergedHistoryEvents}
                    tasks={openTasks}
                    tasksError={openTasksError}
                    onEditProfile={startTenantStammdatenEdit}
                    onGoToTab={setActiveTab}
                  />
                )}
              </DetailTabPanel>

              <DetailTabPanel tabId="tenancies">
                <div className="flex flex-col gap-4 rounded-2xl bg-background p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="m-0 text-[15px] font-semibold tracking-tight text-foreground">
                      Mietverhältnisse
                    </h2>
                    <button
                      type="button"
                      onClick={openAssignForm}
                      className="shrink-0 rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      Mietverhältnis zuweisen
                    </button>
                  </div>
                    {!tenancies.length ? (
                      <p className="m-0 text-[13px] text-muted-foreground">
                        Keine Mietverhältnisse vorhanden
                      </p>
                    ) : (
                      <div className="flex flex-col gap-6">
                        {tenancyEditErr ? (
                          <p className="m-0 text-[13px] text-destructive">
                            {tenancyEditErr}
                          </p>
                        ) : null}
                            {tenancyGroupsOrdered.map(({ key, label, items }) => (
                              <section key={key} className="flex flex-col gap-3">
                                <h3
                                  className="m-0 text-[11px] font-semibold uppercase tracking-[0.7px] text-muted-foreground"
                                  data-testid={`tenancy-group-${key}`}
                                >
                                  {label} ({items.length})
                                </h3>
                                {items.map((tn) => {
                              const rowKey = tn.id != null ? String(tn.id) : `${tn.move_in_date}-${tn.room_id}`;
                              return (
                                <TenancyCard
                                  key={rowKey}
                                  tn={tn}
                                  tenantId={tenantId}
                                  tenant={tenant}
                                  expanded={expandedTenancyIds.has(rowKey)}
                                  onToggleExpand={() => toggleTenancyExpanded(rowKey)}
                                  activityRefreshKey={tenancyActivityKey}
                                  documentsRefreshSignal={documentsRefreshSignal}
                                  tenancyEditingId={tenancyEditingId}
                                  tenancyEditMoveIn={tenancyEditMoveIn}
                                  setTenancyEditMoveIn={setTenancyEditMoveIn}
                                  tenancyEditMoveOut={tenancyEditMoveOut}
                                  setTenancyEditMoveOut={setTenancyEditMoveOut}
                                  tenancyEditCancellationOpen={tenancyEditCancellationOpen}
                                  setTenancyEditCancellationOpen={setTenancyEditCancellationOpen}
                                  terminationMaxIso={unitFixedTermEnd(getUnitById(tn.unit_id))}
                                  tenancyEditActualMoveOut={tenancyEditActualMoveOut}
                                  setTenancyEditActualMoveOut={setTenancyEditActualMoveOut}
                                  // KND-2: a termination save or withdrawal changes the
                                  // row (badge, group, end) and writes tenant + unit
                                  // audit entries — the same two reloads a deposit save
                                  // needs (Regel 49), so the same callback.
                                  onTerminationChanged={reloadAfterDepositSave}
                                  tenancyEditMinimumDurationMonths={tenancyEditMinimumDurationMonths}
                                  setTenancyEditMinimumDurationMonths={setTenancyEditMinimumDurationMonths}
                                  tenancyEditNoticePeriodMonths={tenancyEditNoticePeriodMonths}
                                  setTenancyEditNoticePeriodMonths={setTenancyEditNoticePeriodMonths}
                                  tenancyEditSaving={tenancyEditSaving}
                                  tenancyEditSectionRef={tenancyEditSectionRef}
                                  revenueForm={revenueForm}
                                  setRevenueForm={setRevenueForm}
                                  revenueEditingId={revenueEditingId}
                                  tenancyRevenueByTenancyId={tenancyRevenueByTenancyId}
                                  tenancyRevenueLoadingId={tenancyRevenueLoadingId}
                                  tenancyRevenueErr={tenancyRevenueErr}
                                  inspectionsList={inspectionsList}
                                  inspectionDetailById={inspectionDetailById}
                                  openOrCreateInspection={openOrCreateInspection}
                                  startTenancyEdit={startTenancyEdit}
                                  cancelTenancyEdit={cancelTenancyEdit}
                                  submitTenancyEdit={submitTenancyEdit}
                                  submitRevenueForm={submitRevenueForm}
                                  startRevenueEdit={startRevenueEdit}
                                  cancelRevenueEdit={cancelRevenueEdit}
                                  deleteRevenueRow={deleteRevenueRow}
                                  navigate={navigate}
                                  onEditDeposit={setDepositEditTenancy}
                                  onEditDepositPayout={setDepositPayoutTenancy}
                                  onTenancyCancelled={reloadTenanciesForTenant}
                                  onTenancyReactivated={reloadTenanciesForTenant}
                                  onParticipantsChanged={reloadTenanciesForTenant}
                                />
                              );
                                })}
                              </section>
                            ))}
                      </div>
                    )}
                    {assignOpen ? (
                      <TenancyAssignForm
                        assignUnitsErr={assignUnitsErr}
                        assignErr={assignErr}
                        assignUnitId={assignUnitId}
                        setAssignUnitId={setAssignUnitId}
                        assignRoomId={assignRoomId}
                        setAssignRoomId={setAssignRoomId}
                        assignUnits={assignUnits}
                        assignUnitsLoading={assignUnitsLoading}
                        assignRooms={assignRooms}
                        assignRoomsLoading={assignRoomsLoading}
                        assignUnitTenancies={assignUnitTenancies}
                        assignMoveIn={assignMoveIn}
                        setAssignMoveIn={setAssignMoveIn}
                        assignMoveOut={assignMoveOut}
                        setAssignMoveOut={setAssignMoveOut}
                        assignCancellationOpen={assignCancellationOpen}
                        setAssignCancellationOpen={setAssignCancellationOpen}
                        assignTerminationEffective={assignTerminationEffective}
                        setAssignTerminationEffective={setAssignTerminationEffective}
                        terminationMaxIso={unitFixedTermEnd(getUnitById(assignUnitId))}
                        assignActualMoveOut={assignActualMoveOut}
                        setAssignActualMoveOut={setAssignActualMoveOut}
                        assignNoticeGivenAt={assignNoticeGivenAt}
                        setAssignNoticeGivenAt={setAssignNoticeGivenAt}
                        assignTerminatedBy={assignTerminatedBy}
                        setAssignTerminatedBy={setAssignTerminatedBy}
                        assignMinimumDurationMonths={assignMinimumDurationMonths}
                        setAssignMinimumDurationMonths={setAssignMinimumDurationMonths}
                        assignNoticePeriodMonths={assignNoticePeriodMonths}
                        setAssignNoticePeriodMonths={setAssignNoticePeriodMonths}
                        assignTenantDepositType={assignTenantDepositType}
                        setAssignTenantDepositType={setAssignTenantDepositType}
                        assignTenantDepositAmount={assignTenantDepositAmount}
                        setAssignTenantDepositAmount={setAssignTenantDepositAmount}
                        assignTenantDepositProvider={assignTenantDepositProvider}
                        setAssignTenantDepositProvider={setAssignTenantDepositProvider}
                        assignSecondInlineOpen={assignSecondInlineOpen}
                        setAssignSecondInlineOpen={setAssignSecondInlineOpen}
                        assignSecondRole={assignSecondRole}
                        setAssignSecondRole={setAssignSecondRole}
                        assignSecondForm={assignSecondForm}
                        setAssignSecondForm={setAssignSecondForm}
                        assignSecondMode={assignSecondMode}
                        setAssignSecondMode={setAssignSecondMode}
                        assignSecondSelectedTenant={assignSecondSelectedTenant}
                        onSelectExistingSecond={(t) => {
                          setAssignSecondSelectedTenant(t);
                          setAssignErr(null);
                        }}
                        onClearExistingSecond={() => {
                          setAssignSecondSelectedTenant(null);
                          setAssignSecondSearchQuery("");
                          setAssignSecondSearchResults([]);
                        }}
                        assignSecondSearchQuery={assignSecondSearchQuery}
                        setAssignSecondSearchQuery={setAssignSecondSearchQuery}
                        assignSecondSearchResults={assignSecondSearchResults}
                        assignSecondSearchLoading={assignSecondSearchLoading}
                        assignSecondSearchErr={assignSecondSearchErr}
                        assignRevenueRows={assignRevenueRows}
                        setAssignRevenueRows={setAssignRevenueRows}
                        assignRevenueForm={assignRevenueForm}
                        setAssignRevenueForm={setAssignRevenueForm}
                        assignSaving={assignSaving}
                        onSubmit={handleAssignSubmit}
                        setAssignOpen={setAssignOpen}
                        resetAssignForm={resetAssignForm}
                      />
                    ) : null}
                </div>
              </DetailTabPanel>

              <DetailTabPanel tabId="requirements">
                {/* The tenancy list is handed down rather than fetched again:
                    the tab needs it only to group the requirement rows by
                    contract and to name the viewer's role on each, and the
                    page already holds exactly that list, fetched with the same
                    tenant scope. `onActivityRefresh` bumps the SAME generation
                    every other tenancy mutation bumps — a requirement write
                    lands in the tenancy audit stream, so an open activity feed
                    has to reload; it deliberately does not also refetch the
                    tenancies, which nothing about a requirement write changes. */}
                <TenantRequirementsTab
                  tenantId={tenantId}
                  tenancies={tenancies}
                  onActivityRefresh={() =>
                    setTenancyActivityKey((value) => value + 1)
                  }
                  onGoToTab={setActiveTab}
                />
              </DetailTabPanel>

              <DetailTabPanel tabId="invoices">
                <div className="rounded-2xl bg-background p-4 sm:p-5">
                  <TenantInvoicesTab
                    tenantId={tenantId}
                    active={activeTab === "invoices"}
                    provider={invoiceProvider}
                    capabilitiesLoading={invoiceCapabilitiesLoading}
                    capabilitiesError={invoiceCapabilitiesError}
                  />
                </div>
              </DetailTabPanel>

              <DetailTabPanel tabId="notes">
                <TenantNotesBlock
                  notes={notes}
                  noteDraft={noteDraft}
                  setNoteDraft={(v) => {
                    setNoteDraft(v);
                    setNoteValidationErr(null);
                  }}
                  noteSaving={noteSaving}
                  noteErr={noteValidationErr}
                  noteSubmitError={noteSubmitError}
                  onSubmit={saveNote}
                />
              </DetailTabPanel>

              <DetailTabPanel tabId="documents">
                <div className={sectionCardClass}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "12px",
                      marginBottom: "10px",
                    }}
                  >
                    <div className={sectionTitleClass}>Dokumente</div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "10px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <label className="flex items-center gap-2 text-[13px] text-[#64748b] dark:text-[#6b7a9a]">
                        <span>Kategorie</span>
                        <select
                          value={tenantDocCategory}
                          onChange={(e) => setTenantDocCategory(e.target.value)}
                          disabled={tenantDocUploading || !tenantId}
                          className={`rounded-[9px] border border-black/10 bg-slate-100 px-2 py-1.5 text-[13px] text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff] ${
                            tenantDocUploading || !tenantId
                              ? "cursor-not-allowed opacity-60 dark:bg-white/[0.04]"
                              : "cursor-pointer"
                          }`}
                        >
                          <option value="">—</option>
                          <option value="id_document">Ausweiskopie</option>
                          <option value="debt_register">Betreibungsregisterauszug</option>
                          <option value="insurance">Versicherung</option>
                          <option value="liability_insurance">Privathaftpflicht</option>
                          <option value="employment_contract">Arbeitsvertrag</option>
                          <option value="pay_stub">Lohnabrechnung</option>
                          <option value="residence_permit">Aufenthaltsbewilligung</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </label>
                      <input
                        ref={tenantDocFileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleTenantDocSelected}
                      />
                      <button
                        type="button"
                        onClick={handleTenantDocPick}
                        disabled={tenantDocUploading || !tenantId}
                        className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                        style={{ cursor: tenantDocUploading || !tenantId ? "not-allowed" : "pointer" }}
                      >
                        {tenantDocUploading ? "Wird hochgeladen …" : "Hochladen"}
                      </button>
                    </div>
                  </div>
                  {tenantDocUploadError ? (
                    <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#f87171" }}>
                      {tenantDocUploadError}
                    </p>
                  ) : null}
                  {loading ? (
                    <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Lade Dokumente …</p>
                  ) : tenantDocuments.length === 0 ? (
                    <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Keine Dokumente vorhanden</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className={tableClass}>
                        <thead>
                          <tr>
                            <th className={thCellClass}>Datei</th>
                            <th className={thCellClass}>Typ</th>
                            <th className={thCellClass}>Kategorie</th>
                            <th className={thCellClass}>Datum</th>
                            <th className={thCellClass}>Hochgeladen von</th>
                            <th className={thCellClass}>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentsPagination.pageItems.map((doc) => (
                            <tr key={String(doc.id)}>
                              <td className={`${tdCellClass} font-semibold text-[#0f172a] dark:text-[#eef2ff]`}>{doc.file_name || "—"}</td>
                              <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>{formatTenantDocumentType(doc)}</td>
                              <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                                {formatTenantDocumentCategoryLabel(doc.category)}
                              </td>
                              <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>{formatTenantDocumentDate(doc.created_at)}</td>
                              <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                                {doc.uploaded_by_name != null && doc.uploaded_by_name !== ""
                                  ? doc.uploaded_by_name
                                  : "—"}
                              </td>
                              <td className={tdCellClass}>
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenTenantDocument(doc.id)}
                                    className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#7aaeff] underline hover:text-[#a8c4ff]"
                                    style={{ cursor: "pointer" }}
                                  >
                                    Öffnen
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTenantDocument(doc.id)}
                                    className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#64748b] dark:text-[#6b7a9a] underline hover:text-[#f87171]"
                                    style={{ cursor: "pointer" }}
                                  >
                                    Löschen
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {documentsPagination.pageCount > 1 ? (
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-[11px] text-[#4a5070]">
                        {documentsPagination.page * documentsPagination.pageSize + 1}–{documentsPagination.page * documentsPagination.pageSize + documentsPagination.pageItems.length} von {tenantDocuments.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={documentsPagination.prev}
                          disabled={!documentsPagination.canPrev}
                          className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Zurück
                        </button>
                        <span className="text-[11px] tabular-nums text-[#4a5070]">
                          Seite {documentsPagination.page + 1} / {documentsPagination.pageCount}
                        </span>
                        <button
                          type="button"
                          onClick={documentsPagination.next}
                          disabled={!documentsPagination.canNext}
                          className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Weiter
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </DetailTabPanel>

              <DetailTabPanel tabId="tasks">
                <EntityTasksTab
                  bare
                  filter={{ tenant_id: tenantId }}
                  title="Aufgaben"
                />
              </DetailTabPanel>
              <DetailTabPanel tabId="reports">
                <EntityWorkReportsTab scope="tenant" entityId={tenantId} />
              </DetailTabPanel>
              <DetailTabPanel tabId="history">
            {/* D3: the unified feed is the primary activity view. The
                detailed change log stays below rather than being
                removed — it renders field-level diffs the feed's
                curated allowlist does not reproduce, so deleting it
                would lose function. One tab, no competing second area. */}
                <div className="space-y-4">
                  <EntityActivityFeed
                    entityType="tenant"
                    entityId={tenantId}
                    headingId="tenant-activity-heading"
                  />
                  <TenantHistoryBlock events={mergedHistoryEvents} />
                </div>
              </DetailTabPanel>
            </DetailTabs>
          ) : (
            <p className="px-6 py-5 text-[#4a5070]">Keine Daten.</p>
          )}
        </main>
      </div>
      {inspectionModal ? (
        <InspectionChecklistModal
          modal={inspectionModal}
          onClose={closeInspectionModal}
          onSaveRow={saveInspectionItemRow}
          onUploadPhotos={uploadInspectionItemPhotos}
          onDeletePhoto={deleteInspectionItemPhoto}
          onFinalize={finalizeInspection}
          onGenerateProtocolPdf={generateProtocolPdf}
          onOpenProtocolPdf={openProtocolPdf}
          onSignDual={signInspectionDualAndOpenPdf}
          onOpenSignedPdf={openSignedInspectionPdf}
          unitLabelShort={readableUnitHeaderLabel(inspectionModal?.tenancy)}
          currentUserName={currentUser?.full_name || ""}
        />
      ) : null}

      <TenantMergeModal
        open={mergeOpen}
        survivor={tenant}
        onClose={() => setMergeOpen(false)}
        onMerged={handleTenantMerged}
      />

      {/*
        K4d — ONE mount each, page level, aimed at the selected row.

        Not one pair per tenancy card: several cards are open at once during an
        internal move, and N mounted dialogs for one that can be open is both
        wasteful and a way for two of them to disagree about which row is being
        edited.

        `onSaved` is the refetch the Übersicht used to own: the tenancy PATCH
        both editors send changes the rows, and (Regel 49) writes an audit entry
        on the tenant stream, so the Verlauf has to re-read as well. The server
        answers what the new state is; nothing here guesses it.
      */}
      {depositEditTenancy ? (
        <DepositEditModal
          tenancy={depositEditTenancy}
          open
          onOpenChange={(next) => {
            if (!next) setDepositEditTenancy(null);
          }}
          onSaved={reloadAfterDepositSave}
        />
      ) : null}

      {depositPayoutTenancy ? (
        <DepositPayoutModal
          tenancy={depositPayoutTenancy}
          open
          onOpenChange={(next) => {
            if (!next) setDepositPayoutTenancy(null);
          }}
          onSaved={reloadAfterDepositSave}
        />
      ) : null}
    </div>
  );
}
